import classNames from "classnames";
import React, {
  KeyboardEventHandler,
  MouseEventHandler,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import useSWR, { Fetcher } from "swr";
import { CSSTransition } from "react-transition-group";

import ArrowLeft from "assets/iconsv2/arrow-left-16.svg";
import Globe from "assets/iconsv2/globe-01-16.svg";
import ChevronDown from "assets/iconsv2/chevron-down-16.svg";
import ComputerChip from "assets/iconsv2/computer-chip-16.svg";
import ArrowDownCircle from "assets/iconsv2/arrow-down-circle-16.svg";
import Store from "assets/iconsv2/store-16.svg";
import StarCircle from "assets/iconsv2/star-circle-16.svg";

import abbreviateNumber from "lib/abbreviateNumber";

import styles from "./StoreCommandExample.module.css";

// `/api/v1` extensions differ from `/frontend_api` extensions unfortunately
type User = {
  avatar: string;
};

type Organization = {
  avatar: string;
};

type Command = {
  id: string;
};

type Extension = {
  id: string;
  title: string;
  description: string;
  download_count: number;
  icons: {
    light: string;
    dark: string;
  };
  store_url: string;
  owner: User | Organization;
  author: User;
  commands: Command[];
};

type SectionProps = {
  title: string;
};
const Section: React.FC<PropsWithChildren<SectionProps>> = ({ children, title }) => {
  return (
    <div className={styles.section}>
      <strong>{title}</strong>
      <div className={styles.items}>{children}</div>
    </div>
  );
};

const SectionSkeleton: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className={styles.section}>
      <strong>
        <div className={styles.textSkeleton} style={{ width: 80 }}></div>
      </strong>
      <div className={styles.items}>{children}</div>
    </div>
  );
};

type ExtensionProps = Extension & {
  selected: boolean;
  onMouseEnter: MouseEventHandler;
};

const Extension: React.FC<ExtensionProps> = ({
  title,
  description,
  icons,
  owner,
  author,
  download_count,
  commands,
  selected,
  store_url,
  onMouseEnter,
}) => {
  const avatar = (owner || author).avatar;

  return (
    <a
      className={classNames(styles.extension, { [styles.selected]: selected })}
      onMouseEnter={onMouseEnter}
      href={store_url}
    >
      <Image src={icons.dark || icons.light} width={24} height={24} alt={`${title}'s icon`} />
      <div className={styles.titleSubtitle}>
        <strong>{title}</strong>
        <span>{description}</span>
      </div>
      <div className={styles.accessories}>
        {download_count > 100 ? (
          <div className={styles.accessory} style={{ width: 66 }}>
            <ArrowDownCircle />
            {abbreviateNumber(download_count)}
          </div>
        ) : (
          <div className={classNames(styles.accessory, styles.new)} style={{ width: 66 }}>
            <StarCircle />
            New
          </div>
        )}

        <div className={styles.accessory} style={{ width: 46 }}>
          <ComputerChip />
          {commands.length}
        </div>
        {avatar ? (
          <Image src={avatar} width={16} height={16} alt="avatar" className={styles.avatar} />
        ) : (
          <div className={styles.avatarPlaceholder} />
        )}
      </div>
    </a>
  );
};

const ExtensionSkeleton: React.FC = () => {
  return (
    <div className={styles.extension}>
      <div className={styles.iconSkeleton}></div>
      <div className={styles.titleSubtitle}>
        <span className={styles.textSkeleton} style={{ width: 80 }}></span>
        <span className={styles.textSkeleton} style={{ width: 300 }}></span>
      </div>
      <div className={styles.accessories}>
        <div className={styles.accessory} style={{ width: 66 }}>
          <div className={styles.accessorySkeleton}></div>
          <span className={styles.textSkeleton} style={{ width: 42 }}></span>
        </div>

        <div className={styles.accessory} style={{ width: 46 }}>
          <div className={styles.accessorySkeleton}></div>
          <span className={styles.textSkeleton} style={{ width: 25 }}></span>
        </div>
        <div className={styles.avatarSkeleton} />
      </div>
    </div>
  );
};

const extensionsFetcher: Fetcher<{ data: Extension[] }, string> = async (path) => {
  const response = await fetch(path);
  return await response.json();
};

const StoreCommandExample: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const totalItems = useRef(0);
  const scrollingContainer = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);
  const loadingIndicatorRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const intersectionObserverRef = useRef<IntersectionObserver>();
  const appFrameRef = useRef<HTMLDivElement>(null);

  const { data: allExtensionsData, isValidating: isAllExtensionsValidating } = useSWR(
    shouldLoad ? `/api/v1/store_listings` : null,
    extensionsFetcher,
    { revalidateOnFocus: false }
  );
  const { data: trendingExtensionsData, isValidating: isTrendingExtensionsValidating } = useSWR(
    shouldLoad ? `/api/v1/extensions/trending` : null,
    extensionsFetcher,
    { revalidateOnFocus: false }
  );
  const { data: featuredExtensionsData, isValidating: isFeaturedExtensionsValidating } = useSWR(
    shouldLoad ? `/api/v1/extensions/featured` : null,
    extensionsFetcher,
    { revalidateOnFocus: false }
  );
  const { data: searchResultsData, isValidating: isSearchResultsValidating } = useSWR(
    shouldLoad && searchInput.length > 0 ? `/api/v1/store_listings/search?q=${searchInput}` : null,
    extensionsFetcher,
    { revalidateOnFocus: false }
  );

  const handleIntersect = useCallback<IntersectionObserverCallback>(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && shouldLoad === false) {
          setShouldLoad(true);
        }
      });
    },
    [shouldLoad]
  );

  useEffect(() => {
    intersectionObserverRef.current = new IntersectionObserver(handleIntersect);

    appFrameRef.current && intersectionObserverRef.current.observe(appFrameRef.current);

    return () => {
      intersectionObserverRef.current && intersectionObserverRef.current.disconnect();
    };
  }, [handleIntersect]);

  const isLoading =
    searchInput.length > 0
      ? isSearchResultsValidating
      : isAllExtensionsValidating || isTrendingExtensionsValidating || isFeaturedExtensionsValidating;

  useEffect(() => {
    totalItems.current =
      (allExtensionsData ? allExtensionsData.data.length : 0) +
      (trendingExtensionsData ? trendingExtensionsData.data.length : 0) +
      (featuredExtensionsData ? featuredExtensionsData.data.length : 0);
  }, [allExtensionsData, trendingExtensionsData, featuredExtensionsData]);

  const hasSearchResults = searchInput.length > 0 && searchResultsData;

  const handleKeyDown = useCallback<KeyboardEventHandler>(
    (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        shouldScrollRef.current = true;
        setSelectedIndex(Math.min(selectedIndex + 1, totalItems.current - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        shouldScrollRef.current = true;
        setSelectedIndex(Math.max(0, selectedIndex - 1));
      } else if (event.key === "Enter" && scrollingContainer.current) {
        (scrollingContainer.current.querySelector(`.${styles.selected}`) as HTMLElement)?.click();
      }
    },
    [selectedIndex]
  );

  const scrollExtensionIntoView = useCallback(() => {
    if (shouldScrollRef.current === false) return;
    if (scrollingContainer.current === null) return;

    const element = scrollingContainer.current.querySelector(`.${styles.selected}`);

    if (element === null) return;

    const parentRect = scrollingContainer.current.getBoundingClientRect();
    const childRect = element.getBoundingClientRect();

    if (selectedIndex === 0) {
      scrollingContainer.current.scrollTop = 0;
    } else if (selectedIndex === totalItems.current - 1) {
      scrollingContainer.current.scrollTop = scrollingContainer.current.scrollHeight;
    } else {
      if (childRect.top < parentRect.top) {
        scrollingContainer.current.scrollTop += childRect.top - parentRect.top - 5;
      } else if (childRect.bottom > parentRect.top + scrollingContainer.current.clientHeight) {
        scrollingContainer.current.scrollTop += childRect.bottom - parentRect.bottom + 5;
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    scrollExtensionIntoView();
  }, [selectedIndex, scrollExtensionIntoView]);

  return (
    <div className={styles.appFrame} ref={appFrameRef}>
      <div className={styles.glow}></div>
      <div className={styles.navigationBar}>
        <a className={styles.backButton}>
          <ArrowLeft className="icon" />
        </a>
        <input
          type="text"
          name="searchInput"
          value={searchInput}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          onChange={(event) => {
            setSelectedIndex(0);
            setSearchInput(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          className={styles.searchInput}
          placeholder="Search Store for Extensions"
        />
        <div className={styles.queryDropdown}>
          <Globe style={{ height: 16 }} />
          <span>Public</span>
          <ChevronDown style={{ height: 11 }} />
        </div>
        <CSSTransition
          in={isLoading}
          timeout={300}
          unmountOnExit
          nodeRef={loadingIndicatorRef}
          classNames={{
            enter: styles.fadeEnter,
            enterActive: styles.fadeEnterActive,
            exit: styles.fadeExit,
            exitActive: styles.fadeExitActive,
          }}
        >
          <div className={styles.loadingIndicator} ref={loadingIndicatorRef}></div>
        </CSSTransition>
      </div>
      <div className={styles.content} ref={scrollingContainer}>
        {hasSearchResults ? (
          <Section title="Search Results">
            {searchResultsData.data.map((extension, index) => (
              <Extension
                key={extension.id}
                {...extension}
                selected={selectedIndex === index}
                onMouseEnter={() => {
                  shouldScrollRef.current = false;
                  setSelectedIndex(index);
                }}
              />
            ))}
          </Section>
        ) : !hasSearchResults && featuredExtensionsData && trendingExtensionsData && allExtensionsData ? (
          <>
            <Section title="Featured">
              {featuredExtensionsData.data.map((extension, index) => (
                <Extension
                  key={extension.id}
                  {...extension}
                  selected={selectedIndex === index}
                  onMouseEnter={() => {
                    shouldScrollRef.current = false;
                    setSelectedIndex(index);
                  }}
                />
              ))}
            </Section>
            <Section title="Trending">
              {trendingExtensionsData.data.map((extension, index) => (
                <Extension
                  key={extension.id}
                  {...extension}
                  selected={selectedIndex === index + 3}
                  onMouseEnter={() => {
                    shouldScrollRef.current = false;
                    setSelectedIndex(index + 3);
                  }}
                />
              ))}
            </Section>
            <Section title="All Extensions">
              {allExtensionsData.data.map((extension, index) => (
                <Extension
                  key={extension.id}
                  {...extension}
                  selected={selectedIndex === index + 6}
                  onMouseEnter={() => {
                    shouldScrollRef.current = false;
                    setSelectedIndex(index + 6);
                  }}
                />
              ))}
            </Section>
          </>
        ) : (
          <div className={styles.skeleton}>
            <SectionSkeleton>
              {[...Array(10)].map((_, index) => (
                <ExtensionSkeleton key={index} />
              ))}
            </SectionSkeleton>
          </div>
        )}
      </div>
      <div className={styles.actionBar}>
        <div className={styles.storeIcon}>
          <Store className="icon" />
        </div>
        <span className={styles.actionLabel}>Store</span>
        <div className={styles.actions}>
          <span className={classNames(styles.action, styles.mainAction)}>
            Show Details
            <div className={styles.hotKeys}>
              <i className={styles.hotKey}>↵</i>
            </div>
          </span>
          <span className={classNames(styles.action)}>
            Actions
            <div className={styles.hotKeys}>
              <i className={styles.hotKey}>⌘</i>
              <i className={styles.hotKey}>k</i>
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};

export default StoreCommandExample;
